import Field from "@/components/Field";
import RecordView from "@/components/record/RecordView";
import TaxStatusSelect from "@/components/TaxStatusSelect";

import SettlementTypeSelect from "@/modules/projects/components/SettlementTypeSelect";
import { RoleBasedProjectSettlementTypeService } from "@/modules/projects/service/settlementType";
import { PayrollRoleBasedInspection } from "@/types/payrollRoleBasedInspection";
import { UseQueryResult } from "@tanstack/react-query";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { useValueRenderer } from "@/hooks/useValueRenderer";
import { TextareaWithLimitInfo } from "@/components/TextareaWithLimitInfo";
import { NumberInput } from "@/components/ui/NumberInput";
import { formatNumber } from "@/lib/utils/formatters";
import PayrollVisitCountCorrectedField from "./PayrollVisitCountCorrectedField";

interface PayrollInspectionRoleBasedSectionsProps {
  entryQuery: UseQueryResult<PayrollRoleBasedInspection>;
}

export const PayrollInspectionRoleBasedSections = memo(
  ({ entryQuery }: PayrollInspectionRoleBasedSectionsProps) => {
    const { t } = useTranslation();
    const entry = entryQuery.data;

    // This is an exception, settlement type cannot be editable if it already has been set
    const isSettlementTypeDisabled = !!entry?.settlement_type;

    const renderValue = useValueRenderer();

    return (
      <>
        <RecordView
          query={entryQuery}
          translationKey="payrolls.fieldGroups.basic"
          header={() => t("payrolls.fieldGroups.basic")}
          isOpen={true}
        >
          <RecordView.Field
            name="period"
            label={t("payrolls.fields.period")}
            renderer={() => renderValue(entry?.period)}
          />
          <RecordView.Field
            name="project"
            label={t("payrolls.fields.project")}
            renderer={() => renderValue(entry?.project)}
          />
          <RecordView.Field
            name="store"
            label={t("payrolls.fields.store")}
            renderer={() => renderValue(entry?.store)}
          />
          <RecordView.Field
            name="project_role"
            label={t("payrolls.fields.project_role")}
            renderer={() => renderValue(entry?.project_role)}
          />
        </RecordView>

        <RecordView
          query={entryQuery}
          translationKey="payrolls.fieldGroups.contractor"
          header={() => t("payrolls.fieldGroups.contractor")}
          isOpen={true}
        >
          <RecordView.Field
            name="contractor"
            label={t("payrolls.fields.contractor")}
            renderer={() => renderValue(entry?.contractor)}
          />
          <RecordView.Field
            name="personal_id_number"
            label={t("payrolls.fields.personal_id_number")}
            renderer={() => renderValue(entry?.personal_id_number)}
          />
          <RecordView.Field
            name="agreement_company"
            label={t("payrolls.fields.agreement_company")}
            renderer={() => renderValue(entry?.agreement_company)}
          />
          <RecordView.Field
            name="agreement_status_period"
            label={t("payrolls.fields.agreement_status_period")}
            renderer={() =>
              renderValue(entry?.agreement_status_period, "agreementStatuses")
            }
          />
          <RecordView.Field
            name="tax_status_period"
            label={t("payrolls.fields.tax_status_period")}
            renderer={() => renderValue(entry?.tax_status_period, "taxStatus")}
          />
          <RecordView.Field
            name="tax_status_current"
            label={t("payrolls.fields.tax_status_current")}
            renderer={() => renderValue(entry?.tax_status_current, "taxStatus")}
          />
          <Field
            label={t("payrolls.fields.tax_status_corrected")}
            name="tax_status_corrected"
            render={({ field }) => (
              <TaxStatusSelect {...field} loading="eager" />
            )}
          />
        </RecordView>

        <RecordView
          query={entryQuery}
          translationKey="payrolls.fieldGroups.settlement"
          header={() => t("payrolls.fieldGroups.settlement")}
          isOpen={true}
        >
          <Field
            label={t("payrolls.fields.settlement_type")}
            name="settlement_type"
            render={({ field }) => (
              <SettlementTypeSelect
                settlementTypeService={RoleBasedProjectSettlementTypeService}
                loading="eager"
                {...field}
                disabled={isSettlementTypeDisabled}
                defaultItem={{
                  id: field.value,
                  name: field.value
                    ? t(`settlementType.${field.value}`)
                    : undefined,
                }}
              />
            )}
          />
          <RecordView.Field
            name="hourly_rate"
            label={t("payrolls.fields.hourly_rate")}
            renderer={() => formatNumber(entry?.hourly_rate)}
          />
          <RecordView.Field
            name="monthly_rate"
            label={t("payrolls.fields.monthly_rate")}
            renderer={() => formatNumber(entry?.monthly_rate)}
          />
          <RecordView.Field
            name="visit_fee"
            label={t("payrolls.fields.visit_fee")}
            renderer={() => formatNumber(entry?.visit_fee)}
          />
          <RecordView.Field
            name="total_hours_worked"
            label={t("payrolls.fields.total_hours_worked")}
            renderer={() => formatNumber(entry?.total_hours_worked)}
          />
          <RecordView.Field
            name="hours_worked"
            label={t("payrolls.fields.hours_worked")}
            renderer={() => formatNumber(entry?.hours_worked)}
          />
          <Field
            name="hours_worked_corrected"
            label={t("payrolls.fields.hours_worked_corrected")}
            render={({ field }) => <NumberInput {...field} />}
          />
          <RecordView.Field
            name="hours_worked_final"
            label={t("payrolls.fields.hours_worked_final")}
            renderer={() => formatNumber(entry?.hours_worked_final)}
          />
          <RecordView.Field
            name="total_visit_count"
            label={t("payrolls.fields.total_visit_count")}
            renderer={() => formatNumber(entry?.total_visit_count)}
          />
          <RecordView.Field
            name="visit_count"
            label={t("payrolls.fields.visit_count")}
            renderer={() => formatNumber(entry?.visit_count)}
          />
          <PayrollVisitCountCorrectedField
            hoursBoundToVisits={entry?.hours_bound_to_visits}
            mostFrequentPlannedVisitDuration={
              entry?.most_frequent_planned_visit_duration
            }
          />
          <RecordView.Field
            name="visit_count_final"
            label={t("payrolls.fields.visit_count_final")}
            renderer={() => formatNumber(entry?.visit_count_final)}
          />
        </RecordView>

        <RecordView
          query={entryQuery}
          translationKey="payrolls.fieldGroups.financial"
          header={() => t("payrolls.fieldGroups.financial")}
          isOpen={true}
        >
          <RecordView.Field
            name="base_amount_to_pay"
            label={t("payrolls.fields.base_amount_to_pay")}
            renderer={() => formatNumber(entry?.base_amount_to_pay)}
          />
          <Field
            name="deduction"
            label={t("payrolls.fields.deduction")}
            render={({ field }) => <NumberInput {...field} />}
          />
          <Field
            name="penalty"
            label={t("payrolls.fields.penalty")}
            render={({ field }) => <NumberInput {...field} />}
          />
          <Field
            name="addition"
            label={t("payrolls.fields.addition")}
            render={({ field }) => <NumberInput {...field} />}
          />
          <Field
            name="bonus"
            label={t("payrolls.fields.bonus")}
            render={({ field }) => <NumberInput {...field} />}
          />
          <RecordView.Field
            name="amount_to_pay"
            label={t("payrolls.fields.amount_to_pay")}
            renderer={() => formatNumber(entry?.amount_to_pay)}
          />
        </RecordView>

        <RecordView
          query={entryQuery}
          translationKey="payrolls.fieldGroups.invoicing"
          header={() => t("payrolls.fieldGroups.invoicing")}
          isOpen={false}
        >
          <RecordView.Field
            name="total_limit_hours"
            label={t("payrolls.fields.total_limit_hours")}
            renderer={() => formatNumber(entry?.total_limit_hours)}
          />
          <RecordView.Field
            name="total_invoiced_hours"
            label={t("payrolls.fields.total_invoiced_hours")}
            renderer={() => formatNumber(entry?.total_invoiced_hours)}
          />
          <RecordView.Field
            name="total_invoiced_addition"
            label={t("payrolls.fields.total_invoiced_addition")}
            renderer={() => formatNumber(entry?.total_invoiced_addition)}
          />
          <RecordView.Field
            name="total_invoiced_deduction"
            label={t("payrolls.fields.total_invoiced_deduction")}
            renderer={() => formatNumber(entry?.total_invoiced_deduction)}
          />
          <RecordView.Field
            name="total_invoiced_amount"
            label={t("payrolls.fields.total_invoiced_amount")}
            renderer={() => formatNumber(entry?.total_invoiced_amount)}
          />
          <RecordView.Field
            name="total_invoiced_hours_exceeded"
            label={t("payrolls.fields.total_invoiced_hours_exceeded")}
            renderer={() => formatNumber(entry?.total_invoiced_hours_exceeded)}
          />
        </RecordView>

        <RecordView
          query={entryQuery}
          translationKey="payrolls.fieldGroups.structure"
          header={() => t("payrolls.fieldGroups.structure")}
          isOpen={true}
        >
          <RecordView.Field
            name="manager"
            label={t("payrolls.fields.manager")}
            renderer={() => renderValue(entry?.manager)}
          />
          <RecordView.Field
            name="regional_director"
            label={t("payrolls.fields.regional_director")}
            renderer={() => renderValue(entry?.regional_director)}
          />
          <RecordView.Field
            name="regional_representative"
            label={t("payrolls.fields.regional_representative")}
            renderer={() => renderValue(entry?.regional_representative)}
          />
        </RecordView>

        <RecordView
          query={entryQuery}
          translationKey="payrolls.fieldGroups.comments"
          header={() => t("payrolls.fieldGroups.comments")}
          isOpen={true}
        >
          <RecordView.Field
            name="permanent_comment"
            label={t("payrolls.fields.permanent_comment")}
            renderer={() => renderValue(entry?.permanent_comment)}
          />
          <Field
            name="comment"
            label={t("payrolls.fields.comment_inspection")}
            render={({ field }) => (
              <TextareaWithLimitInfo
                {...field}
                placeholder={t("payrolls.placeholders.comment")}
                maxLength={250}
              />
            )}
          />
        </RecordView>
      </>
    );
  },
);

PayrollInspectionRoleBasedSections.displayName = "PayrollInspectionDetail";
